<template>
  <div class="preloader" v-if="showPreloader">
    <div class="preloader-container">
      <div v-if="configuration.enableSnapshotService" id="snapshot-image-scene"></div>
      <div class="preloader-container-logo">
        <img v-if="customsAssetsEndpoint" :src="`${customsAssetsEndpoint}${passcodeLogo}`" alt="">
      </div>
      <component :is="activePreloaderComponent" v-bind="activeComponentProps"></component>
    </div>
    <new-modal-wrapper
      v-if="configuration.enableNewNoWebglModal"
      :widthModal="'80%'"
      :maxWidthModal="500"
      modal-name="noWebglModal"
      cta-primary-button="oldBrowserCta"
      @cta-action="$refs.content.ctaAction()"
    >
      <no-webgl-modal ref="content"></no-webgl-modal>
    </new-modal-wrapper>
    <modal-wrapper  v-else
      :widthModal="'80%'"
      title="noWebglHeader"
      modal-name="noWebglModal"
      content-component="noWebglModal"
      ctaButton="noWebglBetterBrowser"
    ></modal-wrapper>
  </div>
</template>

<script>
import progressBar from '@/components/Customizer/Preloader/ProgressBar';
import passcode from '@/components/Customizer/Preloader/Passcode';
import baseSpinner from '@/components/Spinner';
import newModalWrapper from '@/components/Modals/NewModalWrapper/NewModalWrapper';
import modalWrapper from '@/components/Modals/ModalWrapper/ModalWrapper';
import noWebglModal from '@/components/Modals/NoWebgl';
import { getLocale } from "@/data/regionInfo";
import replaceUrl from "@/http/replaceUrl";
import selectSwatch from '@/mixins/Customizer/SelectSwatch';
import { external3DScene } from '@/mixins/external3DScene';

export default {
  name: "preloader",
  components: {
    progressBar,
    passcode,
    baseSpinner,
    newModalWrapper,
    modalWrapper,
    noWebglModal
  },
  mixins: [selectSwatch,external3DScene],
  data() {
    return {
      loadings: [
        'loading swatches',
        'loading recipe',
        'loading tabs',
        'loading subgroups',
        'loading palettes',
        'loading palettes',
        'loading ground',
        'loading ground texture',
        'loading 3dModels',
        'loading default textures',
        'loading stitches'
      ],
      /**
       * What component is displayed dynamically
       *
       * @property {?String='passcode', 'progress-bar'} activePreloaderComponent
       */
      activePreloaderComponent: 'base-spinner',
      startLoadings: false,
      finishedLoadingCount: 0,
      percentProgress: 0,
      preloadStarted: false,
    }
  },
  computed: {
    activeComponentProps() {
      if (this.activePreloaderComponent === 'base-spinner') {
        return { position: 'initial' }
      }
      return {}
    },
    showPreloader() {
      return this.$store.state.preloader.showPreloader;
    },
    queryCampaignId() {
      return this.$store.state.utils.queryParams.campaignId;
    },
    configuration() {
      return this.$store.state.utils.configuration;
    },
    customsAssetsEndpoint() {
      return this.$store.state.endPoints.customizer.customsAssets;
    },
    percentAmountPerItem() {
      return 100 / this.loadings.length;
    },
    getWaitingFor() {
      return this.$store.state.wait.waitingFor;
    },
    queryRecipe() {
      return this.$store.state.utils.queryParams.recipe;
    },
    queryPalette() {
      return this.$store.state.utils.queryParams.palette;
    },
    defaultRecipe() {
      return this.$store.state.utils.pdm.default_recipe;
    },
    pdmModel() {
      return this.$store.state.utils.pdmModel;
    },
    availableSwatches() {
      return this.$store.state.endPoints.customizer.availableSwatchesMeta;
    },
    transferredRecipe() {
      return this.$store.state.transferDesign.designData && this.$store.state.transferDesign.designData.recipeId;
    },
    noEsiActive() {
      return this.$store.getters['utils/noEsiActive'];
    },
    useEcomDataLayerFormat() {
      return this.$store.getters['utils/useEcomDataLayerFormat'];
    },
    enablePerformanceLogging() {
      return this.$store.state.utils.queryParams.enablePerformanceLogging || this.$store.state.utils.configuration.enablePerformanceLogging;
    },
    initialTime() {
      return this.$store.state.utils.initialTime;
    },
    passcodeLogo() {
      const actualPalette = this.$store.state.swatches.palettes.find(palette => palette.campaign_id === Number(this.$store.state.preloader.passcode.campaignId))
      return (
        (actualPalette &&
          actualPalette.palette_features.passcode_logo_url) ||
        "/images/common/customs_logo_red.png"
      );
    },
    canvasActive() {
      return this.$store.getters["utils/canvasActive"];
    },
  },
  watch: {
    getWaitingFor: function() {
      if (this.startLoadings) {
        this.finishedLoadingCount = this.loadings.length - this.getWaitingFor.length;
        this.percentProgress = this.percentAmountPerItem * this.finishedLoadingCount;
      } else {
        this.percentProgress = 0;
      }

      // updates the progress so it can be displayed by the progress bar
      this.$store.commit('preloader/SET_PERCENT_PROGRESS', this.percentProgress);
    },
    pdmModel(newValue, oldValue) {
      this.$store.commit('utils/SET_IS_DESIGN_TRANSFERING', true);
      this.$store.commit("legacyNavigation/CLEAR_MENU");
      this.$store.commit('swatches/CLEAR_STATE_IN_TRANSFERING_DESIGN');
      //Reset the standardNavigation menu
      if (this.$store.state.utils.pdm?.product_type === 'apparel') {
        this.$store.dispatch("standardNavigation/UPDATE_STANDARD_NAVIGATION",
        {"activeComponent":'',"activeSubComponent":''})
      }
      new Promise((resolve, reject) => {
        this.$eventBus.$emit("toggle-swatches-view", false);
        this.$store.dispatch('utils/GET_AVAILABLE_SWATCHES',{})
          .then(() => this.$store.dispatch('utils/GET_AVAILABLE_STICKERS'))
          .then(() => this.preloadAssets(resolve, reject, false));
      }).then(() => {
        // It cleans the store if the model has applied embroidery
        this.$store.commit("swatches/SET_DISABLED_SWATCHES_BY_FEATURE", {swatches: [], feature: "personalization"});
        this.$eventBus.$emit("toggle-swatches-view");
        this.$eventBus.$emit("create-3d-object", {
          isTransferDesign: true,
          originalModel: oldValue
        });
        this.$store.commit('transferDesign/IS_CUSTOMIZER_TRANSFERRED_MODEL', true);
        if (this.$store.getters['utils/vsfActive']) {
          this.$eventBus.$emit('customsModelChanged',{prevModel: oldValue, newModel: newValue})
        } else {
          replaceUrl(oldValue, newValue, this.canvasActive);
        }
      })
      .catch((err) => console.error(`Error transfering design ${err}`));
    }
  },
  methods: {
    setDatalayerEventType(productType) {
      const datalayers = [
        '$checkerboardDataLayer',
        '$customizerDataLayer',
        '$embroideryDataLayer',
        '$patternMakerDatalayer',
        '$ugcDataLayer',
        '$ugcEntryPointDataLayer',
      ];
      for (let item of datalayers) {
        this[item]?.updateCategoryType(productType);
      }
    },
    preloadAssets(resolve = () => {}, reject = () => {}, getAvailableAssets = true) {
      // shows the "loading..." message
      this.$store.commit("undo/INIT_COMPONENTS");
      if(this.enablePerformanceLogging)
        console.log("shows the 'loading...' message Ready: ", new Date().getTime() / 1000 - this.initialTime);

      for (let item of this.loadings) {
        this.$wait.start(item); // the "wait/end" messages are sent inside store actions
      }
      this.startLoadings = true;

      if(this.availableSwatches) {
            // starts the loading process
            if (getAvailableAssets) {
              this.$store.dispatch('utils/GET_AVAILABLE_STICKERS');
              if(this.enablePerformanceLogging)
              console.log("AVAILABLE STICKERS Ready: ", new Date().getTime() / 1000 - this.initialTime);
              if (this.configuration.enableStickerGroups) {
                this.$store.dispatch('utils/GET_AVAILABLE_STICKER_GROUPS');
                if(this.enablePerformanceLogging)
                console.log("AVAILABLE STICKERS GROUPS Ready: ", new Date().getTime() / 1000 - this.initialTime);
              }
              this.$store.dispatch('utils/GET_PATTEN_MAKER_AVAILABLE_CONTENT');
            }

            this.$store.dispatch('utils/GET_PDM', {})
              .then(data => {
                if (data.product_type) this.setDatalayerEventType(data.product_type);

                if (this.configuration.enableSkateModels && data.model_redirect) {
                  this.redirectModel();
                }
                if (data.product_type && data.product_type === 'apparel') {
                  this.$store.commit('swatches/CLEAR_STATE_IN_TRANSFERING_DESIGN');
                  this.$store.commit('gdm/CLEAR_CONFIG_STATE_IN_TRANSFER_DESIGN');
                  // apparel load gdm, available swatches including graphics swatches, get recipe
                  this.$store.dispatch('gdm/GET_GDM_CONFIG', {})
                  .then(async (configData) => {
                    if (getAvailableAssets || this.$store.state.transferDesign.designData?.recipeId) {
                      this.$store.dispatch("utils/GET_AVAILABLE_SWATCHES_BY_SUBGROUP", {subgroups: this.configuration.apparelSwatchSubgroups})
                      .then(() => {
                        if(this.enablePerformanceLogging)
                        console.log("AVAILABLE SWATCHES Ready: ", new Date().getTime() / 1000 - this.initialTime);
                      });
                    }
                    await this.getRecipe();
                  })
                }
                else {
                  // non-apparel load available swatches, get recipe
                  if (getAvailableAssets) {
                    this.$store.dispatch('utils/GET_AVAILABLE_SWATCHES', {}).then(() => {
                      if(this.enablePerformanceLogging)
                      console.log("AVAILABLE SWATCHES Ready: ", new Date().getTime() / 1000 - this.initialTime);
                    });
                    }
                  if (this.configuration.enableSkateModels && data.model_redirect) {
                    this.redirectModel();
                  }
                  this.getRecipe();
                }
                this.$store.dispatch("utils/GET_AUDIENCE_SIZES", { partNumber: data.partnumber }).then(sizes => {
              if(this.enablePerformanceLogging)
                console.log("SIZES Ready: ", new Date().getTime() / 1000 - this.initialTime);
            });
            this.$store.dispatch("swatchMaterialGroups/GET_MATERIALS_BY_MODEL").then(groups => {
              if(this.enablePerformanceLogging)
                console.log("swatch Material Groups Ready: ", new Date().getTime() / 1000 - this.initialTime);

              this.$model3D.sceneHelpers.setSwatchMaterialGroupsData({groups});
            });
            this.$store.dispatch("utils/GET_BUCKET_SIZE");
          })
          .then(() => {
            if(this.enablePerformanceLogging)
              console.log("Bucket Size Ready: ", new Date().getTime() / 1000 - this.initialTime);

            if(!this.queryRecipe) {
              this.setActiveComponent(this.$store.state.utils.pdm.default_component);
            }
          }).then(() => {
            resolve();
          })
          .catch(err => {
            console.error('Error preloadAssets:', err);
            reject();
          })
          .catch(err => console.error('Error preloadAssets:', err));
      } else {
        console.error("No available swatches metadata found");
      }
    },
    getRecipe() {
      return new Promise((resolve, reject) => { 
        this.$store.dispatch('recipe/GET_RECIPE', {
          recipe_id: this.transferredRecipe || this.queryRecipe || this.defaultRecipe, setData: true
        }).then((data) => {
          if(this.enablePerformanceLogging)
            console.log("Recipe Ready: ", new Date().getTime() / 1000 - this.initialTime);

          this.setRecipe(data);
          resolve(data);
        })
        .catch(err => {
          if (err.response && (err.response.status === 403 || err.response.status === 404)) {
            this.$modal.show("recipeDeclinedModal");
          } else {
            this.$modal.show("genericErrorModal");
          }
          this.$store.dispatch('recipe/GET_RECIPE', { recipe_id: this.defaultRecipe, setData: true })
            .then((data) => {
            if(this.enablePerformanceLogging)
              console.log("Recipe Ready: ", new Date().getTime() / 1000 - this.initialTime);

            this.setRecipe(data);
            resolve(data)
          });
        });
      })
    },
    /**
     * @function setDetaultRecipe
     * @param {Object} defaultData, Default recipe
     * If defaultData is not null, be sure that defaultRecipe in the store was saved.
     * Otherwise, it will call get default recipe and will save it.
     */
    setDefaultRecipe(defaultData = null){
      this.setActiveComponent(this.$store.state.utils.pdm.default_component);
      if (defaultData) {
        this.$recipe.setDefaultRecipe();
        this.$modal.show('productMissingModal');
        this.getPrice(defaultData);
      } else {
        this.$store.dispatch('recipe/GET_RECIPE', { recipe_id: this.defaultRecipe, setData: true })
          .then(defaultData => {
            (this.enablePerformanceLogging)
              console.log("Default Recipe 2? Ready: ", new Date().getTime() / 1000 - this.initialTime);

            this.$store.commit('recipe/SET_INIT_RECIPE', defaultData);
            this.$store.commit('recipe/SET_DEFAULT_RECIPE', defaultData);
            this.$nextTick(() => this.setSwatchesSelectedFromRecipe(defaultData));
            this.$modal.show('productMissingModal');
            this.getPrice(defaultData);
          });
      }
    },

    /**
     * @function setRecipe
     * @param {Object} data, Precreated recipe object
     * Validates if the precreated is a valid recipe and save it as init recipe.
     * If one of these configuration flags(personalization, enablePatternMaker, enableUGC)
     * is false and the precreated recipe has that feature, it needs to save the default recipe
     * as init recipe. If none flag is false, the precreated recipe will be saved
     * as init recipe successfully. Otherwise it will have the default recipe.
     */
    setRecipe(data) {
      if (this.$recipe.recipeMatchesPDM() && (this.transferredRecipe || this.queryRecipe)) {
        // Recipe Matches PDM and is precreated
        this.$store.commit('recipe/SET_INIT_RECIPE', data);
        this.$store.dispatch('recipe/GET_RECIPE', { recipe_id: this.defaultRecipe })
          .then(defaultData => {
            this.$store.commit('recipe/SET_DEFAULT_RECIPE', defaultData);
            this.$nextTick(() => this.setSwatchesSelectedFromRecipe(data));
            const hasPersonalizationException = !this.$store.getters["utils/isPersonalizationEnabled"] && this.$recipe.checkForPersonalization();
            const hasUGCException = this.$recipe.checkUGCByType({
              includePatternMaker: !this.configuration.vct_settings.pattern_maker.enabled,
              includeImageLibrary: !this.$store.getters["utils/isImageLibraryEnabled"],
              excludeImageLibrary: this.$store.getters["utils/isImageLibraryEnabled"]
            });
            if (hasPersonalizationException || hasUGCException) {
              this.setDefaultRecipe(defaultData);
            } else {
              this.setActiveComponent(this.$recipe.getRecipeDefaultComponent());
              this.setSwatchesSelected(defaultData, data);
              this.getPrice(data);
            }
          });
      } else if (this.queryRecipe) {
        // Recipe Does NOT Matches PDM and is precreated
        this.setDefaultRecipe();
      } else {  // Default recipe
        this.$store.commit('recipe/SET_INIT_RECIPE', data);
        this.$store.commit('recipe/SET_DEFAULT_RECIPE', data);
        this.$nextTick(() => this.setSwatchesSelectedFromRecipe(data));
        this.getPrice(data);
      }
    },
    getPrice(data) {
      this.$store.dispatch('wait/end', 'loading recipe', { root: true });
      this.$store.dispatch("buyPanel/GET_PRICE", { recipe: data })
        .then((values) => {
          if(this.enablePerformanceLogging)
            console.log("Get Price Ready: ", new Date().getTime() / 1000 - this.initialTime);
          this.setDataLayer(values);
        })
    },
    setSwatchesSelected(defaultRecipe, initRecipe) {
      if (!defaultRecipe.components || !initRecipe.components) {
        return;
      }
      Object.keys(initRecipe.components).forEach(component => {
        const { group, subgroup, swatch_id, sub_components } = initRecipe.components[component];
        if (group !== 'upgradeoptions' && JSON.stringify(defaultRecipe.components[component]) !== JSON.stringify(initRecipe.components[component])) {
          const data = { swatchId: swatch_id.toString(), group, subgroup };
          this.$store.commit("swatches/SET_SWATCHES_SELECTED", { component: component, data });
        }
        if(sub_components) {
          Object.keys(sub_components).forEach(sub_component => {
            const { group: sub_component_group, subgroup: sub_component_subgroup, swatch_id: sub_component_swatch_id } = initRecipe.components[component].sub_components[sub_component];
            const sub_component_data = { swatchId: sub_component_swatch_id.toString(), sub_component_group, sub_component_subgroup };
            this.$store.commit("swatches/SET_SWATCHES_SELECTED", { component: `${component}/${sub_component}`, data: sub_component_data });
          });
        }
      });
    },
    setDataLayer({ priceInDecimal, priceInString, currency }) {
      const { pdm, configuration, audienceSizes  } = this.$store.state.utils;
      const { itemsToPurchase, sku } = this.$store.state.buyPanel;
      const { recipeId } = this.$store.state.recipe;
      const products = [];
      const productDetailLabelId = pdm.partnumber;
      const brand = configuration.region;
      const availableSizes = audienceSizes.identifier;

      const baseData = {
        preCreatedCustomCode: this.$store.state.utils.queryParams.preCreatedCustomCode || null,
        recipeId
      }
      const productData = {
        preCreatedCustomCode: this.$store.state.utils.queryParams.preCreatedCustomCode,
        recipeID: this.$store.state.utils.queryParams.recipeID || recipeId,
        styleCode: this.$store.state.utils.queryParams.preCreatedCustomCode,
        id: this.$store.state.utils.queryParams?.id
      }
      if (this.useEcomDataLayerFormat) {
        itemsToPurchase.forEach((item) => {
          products.push({
            brand: `VANS-${ getLocale().split('_').pop().toUpperCase() }`,
            id: '',
            name: pdm.product_name,
            customsModel: pdm.product_name,
            bundleID: productDetailLabelId,
            price: Number(priceInDecimal),
            availSizes: availableSizes,
            colorCode: '',
            originalPrice: '',
            saleDiscountAmount: 0,
            variant: '',
            avgRating: 0,
            colorDescription: '',
            onSale: false,
            season: '',
            styleCode: '',
            customized: true,
            ...productData
          })
        });
        const dataLayerSource = this.canvasActive ? parent.dataLayer : dataLayer;
        dataLayerSource.push({
          event: 'productDetailView',
          eventCategory: "Customs",
          eventAction: "Product Detail View",
          eventLabel: productDetailLabelId,
          ...baseData,
          nonInteractive: false,
          ecommerce: {
            currencyCode: currency,
            detail: {
              actionField: {},
              products
            }
          },
          customMetrics: {},
          customVariables: {}
        });

      } else {
        itemsToPurchase.forEach(({ size, quantity }) => products.push({
          name: pdm.product_name,
          id: recipeId,
          bundleID: pdm.partnumber,
          productID: recipeId,
          itemID: sku,
          vn: sku,
          price: Number(priceInDecimal),
          brand: 'vans',
          category: '',
          variant: '',
          quantity,
          styleCode: '',
          colorCode: '',
          colorDescription: '',
          size,
          ...productData
        }));

        dataLayer.push({
          event: 'productDetailView',
          ...baseData,
          ecommerce: {
            currencyCode: currency,
            currencySymbol: priceInString.replace(priceInDecimal, ''),
            detail: {
              actionField: { list: 'customizer' },
              products
            }
          }
        });
      }
    },
    setActiveComponent(componentName) {
      // TODO: tempData/SET_ACTIVE_UGC_COMPONENT needs a default UGCIndex to work & the other feature won't have a component
      this.$store.commit('ugc/tempData/SET_ACTIVE_UGC_COMPONENT', componentName);
      this.$store.commit('legacyNavigation/SET_ACTIVE_COMPONENT', componentName);
      this.$store.dispatch(
        'standardNavigation/UPDATE_STANDARD_NAVIGATION',
        { 'activeComponent': componentName, 'activeSubComponent': '' },
      );
    },
    /**
     * @function setPreloaderActiveComponent
     * Set the active component, could be(base-spinner, progress-bar, passcode).
     * If the componentName is progress-bar starts the normal flow.
     * It is linked to preloader-set-active-component eventBus.
     * @param {String} componentName
     */
    setPreloaderActiveComponent(componentName) {
      this.activePreloaderComponent = componentName;
      if (this.activePreloaderComponent === 'progress-bar') {
        this.preloadAssets();
      }
    },
    /**
     * @function getCampaignId
     * Validates if the palette wasn't donwloaded before and add it
     * to the promise.
     * Once the responses are done. It evaluates each response to
     * determine if there is a campaign id in the palette.
     * If there is one resolve the promise with the campaign id number.
     * Otherwise resolve with null value.
     * @param {Array} palettesIds, Array of palettes id numbers.
     */
    getCampaignId(palettesIds) {
      if (palettesIds.length == 0 ) return Promise.resolve();
      if (palettesIds.length === 0 || Array.isArray(palettesIds) === false) return Promise.resolve();
      let promises = [];
      const palettesIdSaved = this.$store.state.swatches.palettesId;
      palettesIds.forEach((palette) => {
        if (!palettesIdSaved.includes(Number(palette))) {
          promises.push(this.$store.dispatch("swatches/GET_PALETTES", palette)
            .catch(err => console.error('Get Campaign palette error: ', err)));
        }
      });
      return new Promise((resolve, reject) => {
        Promise.all(promises).then((palettes) => {
          if(this.enablePerformanceLogging)
            console.log("Palettes Ready: ", new Date().getTime() / 1000 - this.initialTime);
          const newPalettesId = [... new Set(palettesIds)];
          this.$store.commit("swatches/SET_PALETTES_ID", newPalettesId);
          this.$store.dispatch('wait/end', 'loading palettes', { root: true });
          for(let palette of palettes) {
            if (palette && !!palette.campaign_id) {
              return resolve(palette.campaign_id);
            }
          };
          return resolve(null);
        });
      });
    },
    redirectModel(){
      this.$modal.show("replacementModal");
    },
    setTranseredRecipe(value = null) {
      this.transferredRecipe = value;
    },
    async renderStartOverImage() {
      if(this.$store.state.utils.queryParams.slim) return;
      await this.$nextTick();
      await this.$nextTick();

      const startOverImgData = await this.initExternal3Dscene({domEl: "snapshot-image-scene", snapshotSize: "md", snapshotView: "checkerboard_creator", showSnapShot: true});
      this.$store.commit('modals/SET_START_OVER_PRODUCT_IMAGE', {
        recipeId: this.queryRecipe || this.defaultRecipe,
        data: startOverImgData.fileUrl
      });
    }
  },
  /**
   * If campaignId is in url, the active component will be passcode.
   * Otherwise, it will get the palettes from the url if they exists.
   * In case there  isn't palette in the url and there is a recipe in the url
   * it will look at the recipe.
   * Finally if there is not recipe in the url it will use the palette array from
   * the pdm.
   * If one of the palettes used has campaing id, it will show the passcode component.
   * If there is not campaign_id in the palettes used, it will show the preloader component.
   * Keep in mind that the palettes has already been donwload. It means that
   * in the palette component will get only the palettes that were not downloaded.
   */
  created() {
    this.$store.dispatch('translations/FETCH_TRANSLATIONS', {type: 'customizer'}).then(() => {
      this.$store.dispatch('translations/FETCH_UPCHARGE', {type: 'customizer'}).then(() => {
        if (this.queryCampaignId) {
          this.$store.commit('preloader/passcode/SET_CAMPAIGN_ID', this.queryCampaignId);
          this.activePreloaderComponent = 'passcode';
        } else {
          const palettesIds = this.queryPalette ? this.queryPalette.split("|").map(Number) : [];
          const default_palettes = this.configuration.default_palettes[this.pdmModel];
          let paletteKey;
          this.getCampaignId(palettesIds)
            .then((paletteCampaignId) => {
              if (paletteCampaignId) return paletteCampaignId;
              if (!paletteCampaignId && this.queryRecipe) {
                paletteKey = 'palettes';
                return this.$store.dispatch(
                  'recipe/GET_RECIPE', { recipe_id: this.queryRecipe }
                );
              } else {
                paletteKey = 'default_palettes';
                // It is needed to get the default palettes from the pdm,
                // the pdm won't be saved in the vuex since there is some
                // watchers that are waiting for this value
                // but at this point is not needed for all the normal flow.
                return default_palettes;
              }
            })
            .then((data) => {
              // Data is either an object or paletteCampaignId at this point.
              // If it is data it needs to call palettes that come from either recipe or pdm.
              // Otherwhise just return paletteCampaingId as data.
              if (typeof data === 'object') {
                // It's possible that either the pdm or the precreated doesn't have
                // palettes keys
                if (data[paletteKey] && Array.isArray(data[paletteKey])) {
                  data[paletteKey].push(...default_palettes);
                  return this.getCampaignId(data[paletteKey]);
                } else if (Array.isArray(data)) {
                  return this.getCampaignId(data);
                } else {
                  return null;
                }
              }
              return data;
            })
            .then((paletteCampaignId) => {
              if (paletteCampaignId) {
                this.$store.commit('preloader/passcode/SET_CAMPAIGN_ID', paletteCampaignId);
                this.setPreloaderActiveComponent('passcode');
              } else {
                this.setPreloaderActiveComponent('progress-bar');
              }
            })
            .catch((error) => {
              this.setPreloaderActiveComponent('progress-bar');
              console.error(error);
            });
        }
      }).catch(err => {
        console.error('Error to get upcharge:', err);
      });
    }).catch(err => {
      console.error('Error to get translations:', err);
    });
    this.$eventBus.$once('preloader-set-active-component', this.setPreloaderActiveComponent);
    this.$eventBus.$once('render-start-over-image', this.renderStartOverImage);
  },
  beforeDestroy() {
    console.log("deactivated")
  }
}
</script>
